// Reusable Brand DNA Scan component
// Displays a card with input field and button for analyzing a website's brand DNA
// Includes integration with WebsiteAnalysisModal for showing loading/analysis states
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import dnaIcon from '@/assets/images/dna_icon.png';
import WebsiteAnalysisModal from '@/components/custom-ui/homepage_loader';

interface BrandDnaScanProps {
  className?: string;
  showTitle?: boolean;
  showDescription?: boolean;
}

export default function BrandDnaScan({
  className = '',
  showTitle = true,
  showDescription = true
}: BrandDnaScanProps) {
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [sessionToken] = useState(() => `anon_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`);

  const handleAnalyzeWebsite = async () => {
    if (!websiteUrl.trim()) return;

    setIsAnalyzing(true);

    try {
      const workersUrl = import.meta.env.VITE_WORKERS_URL || 'http://localhost:3000';
      const response = await fetch(`${workersUrl}/rainfactory/anonymous-brands/create_anonymous_brand`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          url: websiteUrl,
          sessionToken: sessionToken
        })
      });

      const result = await response.json();

      if (response.ok) {
        // Use the session token returned by the backend (might be existing brand's token)
        const actualSessionToken = result.session_token || sessionToken;
        // Store session token for later use
        localStorage.setItem('anonymous_session_token', actualSessionToken);
        // Redirect to onboarding with the anonymous brand data
        window.location.href = `/onboarding/help-wizard?anonymous=true&sessionToken=${actualSessionToken}`;
      } else {
        console.error('Failed to analyze website:', result.error);
        alert('Failed to analyze website. Please try again.');
        setIsAnalyzing(false);
      }
    } catch (error) {
      console.error('Error analyzing website:', error);
      alert('An error occurred. Please try again.');
      setIsAnalyzing(false);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className={`mb-16 ${className}`}
      >
        <div className="w-full max-w-xl mx-auto">
          {/* Title and Description Above the Card */}
          {(showTitle || showDescription) && (
            <div className="mb-8">
              {showTitle && (
                <div className="text-center mb-6">
                  <div className="flex justify-center mb-4">
                    <img
                      src={dnaIcon}
                      alt="DNA Icon"
                      className="h-12 w-12 sm:h-14 sm:w-14"
                    />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-700 via-purple-700 to-indigo-700 bg-clip-text text-transparent">
                    Experience a Convergence: Your DNA + The Zeitgeist
                  </h2>
                </div>
              )}

              {showDescription && (
                <div className="max-w-2xl mx-auto space-y-4 text-left">
                  <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                    Our AI can connect to your brand in seconds—try it now! Give it your URL and it will scan your website and social media to create a deep profile and analysis including:
                  </p>

                  <ul className="space-y-2.5 text-sm sm:text-base text-gray-700 pl-1">
                    <li className="flex items-start gap-2.5">
                      <span className="text-blue-600 font-semibold mt-0.5">•</span>
                      <span className="leading-relaxed">Brand voice & tone</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="text-purple-600 font-semibold mt-0.5">•</span>
                      <span className="leading-relaxed">Products & services</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="text-indigo-600 font-semibold mt-0.5">•</span>
                      <span className="leading-relaxed">Market positioning</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="text-blue-600 font-semibold mt-0.5">•</span>
                      <span className="leading-relaxed">Target audience insights</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="text-blue-600 font-semibold mt-0.5">•</span>
                      <span className="leading-relaxed">Strengths and weaknesses</span>
                    </li>
                  </ul>

                  <p className="text-base sm:text-lg text-gray-800 font-semibold pt-2">
                    Try it now! See your free analysis in about 30 seconds.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Embedded Modal-like Card */}
          <motion.div
            whileHover={{ y: -4, scale: 1.02 }}
            className="relative group"
          >
            {/* Enhanced glow effect background */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 via-purple-500/30 to-indigo-500/30 rounded-2xl blur-2xl opacity-60 group-hover:opacity-80 transition-opacity duration-500" />
            <div className="absolute inset-0 bg-gradient-to-br from-blue-400/10 via-purple-400/10 to-indigo-400/10 rounded-2xl blur-xl" />

            <Card className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/40 to-indigo-50/50 backdrop-blur-sm border-2 border-blue-300/60 shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:scale-105 hover:-translate-y-2">
              <div className="absolute inset-0 bg-grid-pattern opacity-5" />
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-16 translate-x-16" />
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-purple-100/30 to-transparent rounded-full translate-y-12 -translate-x-12" />
              <CardContent className="relative space-y-4 px-4 sm:px-6 py-6">
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-2">
                  <Input
                    type="url"
                    placeholder="https://yourwebsite.com"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    className="flex-1 h-12 text-base bg-white/70 backdrop-blur-sm border-blue-300/50 focus:border-blue-500/80 focus:bg-white/90 transition-all duration-300 shadow-sm hover:shadow-md"
                    onKeyDown={(e) => e.key === 'Enter' && handleAnalyzeWebsite()}
                  />
                  <div className="relative w-full sm:w-auto">
                    {/* Glow effect behind button */}
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg blur-xl opacity-60 animate-pulse" />
                    <Button
                      onClick={handleAnalyzeWebsite}
                      disabled={!websiteUrl.trim() || isAnalyzing}
                      size="lg"
                      className="relative w-full sm:w-auto sm:min-w-[140px] h-12 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-bold shadow-2xl hover:shadow-[0_0_30px_rgba(99,102,241,0.6)] transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 border-0 ring-2 ring-white/30 hover:ring-white/50 disabled:opacity-100 disabled:cursor-not-allowed"
                    >
                      {isAnalyzing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Scanning...
                        </>
                      ) : (
                        <>
                          Scan Brand DNA
                          <ArrowRight className="h-4 w-4 mr-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-gray-700 text-center px-2 leading-relaxed font-medium">
                  <span className="inline-block px-3 py-1.5 rounded-lg bg-white/60 backdrop-blur-sm border border-white/40 shadow-sm">
                    No signup required • Get instant results • See your brand strategy kit
                  </span>
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </motion.div>

      {/* Website Analysis Modal */}
      <WebsiteAnalysisModal
        isOpen={isAnalyzing}
        onClose={() => setIsAnalyzing(false)}
        websiteUrl={websiteUrl}
      />
    </>
  );
}
